import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {Panel} from 'primeng/panel';
import {TableModule} from 'primeng/table';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {Divider} from 'primeng/divider';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService} from 'primeng/api';
import {MessageService} from 'primeng/api';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import { CommonEffects } from '../store/common.effects';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectAssetCodesGroupDistinctData, selectAssetCodesGroupDistinctTotal } from '../store/common.selector';
import { CatalogAssetCodesGroupDistinctResponse } from '../../shared/models/common/asset-codes-group-distinct.model';
import { selectIsLoadingAction } from '../../shared/store/global';
import { takeUntil } from 'rxjs/operators';
import * as action from '../store/common.actions';
import {toSignal} from '@angular/core/rxjs-interop';
import {BaseListComponent} from '../../shared/components/base-list.component';
import { AssetHistoryDialogComponent } from '../../shared/components/asset-history-dialog/asset-history-dialog.component';
import { ColumnFilters } from '../../shared/models/ui/table-column.model';

@Component({
  selector: 'asset-codes-group-distinct-list',
  templateUrl: './asset-codes-group-distinct-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Divider,
    ConfirmDialogModule,
    DialogModule,
    ToastModule,
    AssetHistoryDialogComponent
  ],
  providers: [ConfirmationService, MessageService, CommonEffects],
  styleUrls: ['./asset-codes-group-distinct-list.component.scss']
})
export class AssetCodesGroupDistinctListComponent extends BaseListComponent implements OnInit, OnDestroy {

  private messageService = inject(MessageService);
  private store$ = inject(Store<{ common: any }>)
  private actions$ = inject(Actions);

  assetCodesGroupDistinct = toSignal(this.store$.select(selectAssetCodesGroupDistinctData), {initialValue: []});
  totalRecords = toSignal(this.store$.select(selectAssetCodesGroupDistinctTotal), {initialValue: 0});
  loading = toSignal(this.store$.select(selectIsLoadingAction('[Common] Find Asset Codes Group Distinct')), {initialValue: false});

  
  readonly auditResourceName = 'Asset Codes Group Distinct';
  historyVisible = false;
  historyRecordId: number | string | null = null;
  historyRecordLabel = '';

  selectedAssetCodesGroupDistincts = signal<CatalogAssetCodesGroupDistinctResponse[]>([]);

  // Add Asset Codes Group Distinct Form Variables
  dialogVisible = signal(false);
  assetGroupName = signal('');
  assetGroupCode = signal('');
  savingAssetCodesGroupDistinct = signal(false);
  isEditMode = signal(false);
  editingAssetCodesGroupDistinctId = signal<number | null>(null);

  deleteButtonLabel = computed(() => {
    return this.selectedAssetCodesGroupDistincts().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  override ngOnInit(): void {
    super.ngOnInit();

    // Success Alert
    this.actions$.pipe(
      ofType(action.createAssetCodesGroupDistinctSuccess, action.updateAssetCodesGroupDistinctSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingAssetCodesGroupDistinct.set(false);
      this.closeDialog();
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    })

    // Delete Success Alert
    this.actions$.pipe(
      ofType(action.deleteAssetCodesGroupDistinctSuccess, action.bulkDeleteAssetCodesGroupDistinctSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.selectedAssetCodesGroupDistincts.set([]);
      this.loadAssetCodesGroupDistinct(); 
    })

    // Handle Error
    this.actions$.pipe(
      ofType(action.createAssetCodesGroupDistinctError, action.updateAssetCodesGroupDistinctError, action.deleteAssetCodesGroupDistinctError, action.bulkDeleteAssetCodesGroupDistinctError),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingAssetCodesGroupDistinct.set(false);
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    const keywords = filters['keywords'] || '';
    this.store$.dispatch(action.findAssetCodesGroupDistinct({
      page,
      size,
      keywords
    }));
  }

  loadAssetCodesGroupDistinct(): void {
    this.store$.dispatch(action.findAssetCodesGroupDistinct({ page: 1, size: this.rows, keywords: ''}))
  }

  addNew(): void {
    this.assetGroupName.set('');
    this.assetGroupCode.set('');
    this.isEditMode.set(false);
    this.dialogVisible.set(true);
    this.editingAssetCodesGroupDistinctId.set(null);
  }

  closeDialog(): void {
    this.assetGroupName.set('');
    this.assetGroupCode.set('');
    this.savingAssetCodesGroupDistinct.set(false);
    this.isEditMode.set(false);
    this.dialogVisible.set(false);
    this.editingAssetCodesGroupDistinctId.set(null);
  }

  saveAssetCodesGroupDistinct(): void {
    if(!this.assetGroupName().trim() || !this.assetGroupCode().trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Asset group name and asset group code are required'
      });
      return;
    }

    this.savingAssetCodesGroupDistinct.set(true);

    const assetCodesGroupDistinctData = {
      asset_group_name: this.assetGroupName().trim(),
      asset_group_code: this.assetGroupCode().trim()
    };

    if(this.isEditMode() && this.editingAssetCodesGroupDistinctId()) {
      this.store$.dispatch(action.updateAssetCodesGroupDistinct({ 
        id: this.editingAssetCodesGroupDistinctId()!, 
        assetCodesGroupDistinct: assetCodesGroupDistinctData 
      }))
    } else {
      this.store$.dispatch(action.createAssetCodesGroupDistinct({ 
        assetCodesGroupDistinct: assetCodesGroupDistinctData 
      }))
    }
  }

  editAssetCodesGroupDistinct(): void {
    if (this.selectedAssetCodesGroupDistincts().length === 1) {
      const assetCodesGroupDistinctToEdit = this.selectedAssetCodesGroupDistincts()[0];
      this.assetGroupName.set(assetCodesGroupDistinctToEdit.asset_group_name ? assetCodesGroupDistinctToEdit.asset_group_name : '');
      this.assetGroupCode.set(assetCodesGroupDistinctToEdit.asset_group_code ? assetCodesGroupDistinctToEdit.asset_group_code : '');
      this.isEditMode.set(true);
      this.editingAssetCodesGroupDistinctId.set(assetCodesGroupDistinctToEdit.id);
      this.dialogVisible.set(true);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one asset codes group distinct to edit'
      });
    }
  }

  deleteAssetCodesGroupDistinct(): void {
    if (this.selectedAssetCodesGroupDistincts().length === 0) {
      return;
    }

    const assetCodesGroupDistinctsToDelete = [...this.selectedAssetCodesGroupDistincts()];
    const count = assetCodesGroupDistinctsToDelete.length;
    
    let message: string;
    if (count === 1) {
      message = `Are you sure you want to delete "${assetCodesGroupDistinctsToDelete[0].asset_group_name}" (${assetCodesGroupDistinctsToDelete[0].asset_group_code})?`;
    } else {
      const maxDisplay = 10;
      const codesToShow = assetCodesGroupDistinctsToDelete.slice(0, maxDisplay);
      const codesList = codesToShow.map((ag, index) => `${index + 1}. ${ag.asset_group_code} - ${ag.asset_group_name}`).join('<br>');
      
      let fullMessage = `Are you sure you want to delete ${count} asset codes groups?<br><br><strong>Asset groups to be deleted:</strong><br>${codesList}`;
      
      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }
      
      message = fullMessage;
    }

    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (count === 1) {
          this.store$.dispatch(action.deleteAssetCodesGroupDistinct({ id: assetCodesGroupDistinctsToDelete[0].id }));
        } else {
          const ids = assetCodesGroupDistinctsToDelete.map(ag => ag.id);
          this.store$.dispatch(action.bulkDeleteAssetCodesGroupDistinct({ ids }));
        }
      }
    });
  }

  refreshAssetCodesGroupDistinct(): void {
    this.selectedAssetCodesGroupDistincts.set([]);
    this.clearFilter();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'Asset codes groups list has been refreshed'
    });
  }

  showHistory(): void {
    const selected = this.selectedAssetCodesGroupDistincts();
    if (selected.length !== 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'History',
        detail: 'Select one record to view history'
      });
      return;
    }
    const item = selected[0] as any;
    this.historyRecordId = item?.id ?? null;
    this.historyRecordLabel = item?.name || item?.group_name || item?.code || String(this.historyRecordId ?? '');
    this.historyVisible = true;
  }
}
